import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import DynamicForm, { DynamicFormRef, makeFormInputsUneditable, setCurrencySymbolForMoneyInputs } from "@mavichpe/dynamic-forms";
import { showAlert } from "@utils/Alert";
import { CurrencyIdPubSub, CurrencyUpdatePubSubPayload } from "@utils/pubsubs/PubSub";
import { CurrencyApi } from "@/api/CurrencyApi";
import { CURRENCIES_SYMBOLS } from "@utils/Constants";
import { useDispatch } from "react-redux";
import { setCurrency } from "@/store/slices/currencySlides";
import { omitProperties, removeFunctionFromObject } from "@utils/Utils";

// Tipos genéricos

export type ConfirmationConfig = {
    message: string | React.ReactNode
}
interface BaseFormProps<TFormData, TApiData> {
    id?: string;
    copyData?: boolean
    copyExcludeProps?: Array<keyof TApiData>,
    formId: string;
    unEditableFields?: Array<keyof TFormData>,
    modelRelativeObjects?: Array<keyof TFormData>,
    saveRedirectUrl?: string;
    formConfig: any;
    getNextCode?: () => Promise<Number>;
    getById?: (id: string) => Promise<TApiData>;
    createFn?: (data: TApiData) => Promise<TApiData>;
    updateFn?: (id: string, data: TApiData) => Promise<TApiData>;
    successMessage?: string;
    errorMessage?: string;
    defaultData?: TApiData;
    children?: React.ReactNode;
    confirmationConfig?: ConfirmationConfig,
    onInputChange?: (name: keyof TFormData, value: any, event?: React.ChangeEvent<any>) => void;
    onInputKeyDown?: (name: keyof TFormData, value: any, event?: React.KeyboardEvent<any>) => void;
    onStored?: (clientData?: TApiData) => void
}

const GenericFormInner = <TFormData extends { id?: string | null, codigo?: any, currencyId?: any }, TApiData extends Partial<TFormData>>(
    props: BaseFormProps<TFormData, TApiData>,
    ref?: React.Ref<DynamicFormRef>
) => {
    const {
        id,
        formId,
        saveRedirectUrl,
        onStored,
        formConfig,
        getById,
        getNextCode,
        createFn,
        updateFn,
        successMessage,
        errorMessage,
        defaultData,
        children,
        copyData = false,
        copyExcludeProps = [],
        onInputChange,
        onInputKeyDown,
        unEditableFields,
        modelRelativeObjects = [],
        confirmationConfig
    } = props;

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [currencySymbol, setCurrencySymbol] = useState<string>("₡");
    const [initialData, setInitialData] = useState<TApiData>({} as TApiData);
    const { currencyId } = initialData;

    const internalRef = useRef<DynamicFormRef>(null);
    const formRef = (ref && typeof ref !== 'function') ? ref : internalRef;

    const resolvedFormConfig = useMemo(() => {
        let config = {
            ...formConfig,
        };

        if (unEditableFields && unEditableFields.length > 0) {
            config = makeFormInputsUneditable(config, unEditableFields);
        }

        if (currencySymbol) {
            config = setCurrencySymbolForMoneyInputs(config, currencySymbol);
        }

        return config;
    }, [formConfig, currencySymbol, unEditableFields]);

    /**
     * DynamicForm sincroniza externalFormState en layout, pero Formik puede quedar sin `codigo`
     * (p. ej. doble montaje en Strict Mode o tras setFormStructure). Reaplicamos solo si el
     * valor del formulario no coincide con initialData en modo creación.
     */
    useEffect(() => {
        if (id) {
            return;
        }
        const raw = (initialData as { codigo?: unknown }).codigo;
        if (raw === undefined || raw === null) {
            return;
        }
        const expected = typeof raw === "number" ? raw : Number(raw);
        if (Number.isNaN(expected)) {
            return;
        }

        const syncCodigoIfNeeded = () => {
            const inst = formRef.current;
            if (!inst) {
                return;
            }
            void inst.getFormData(false).then((fd) => {
                const cur = (fd as { codigo?: unknown })?.codigo;
                const curNum =
                    cur === undefined || cur === null || cur === ""
                        ? NaN
                        : typeof cur === "number"
                          ? cur
                          : Number(cur);
                if (!Number.isNaN(curNum) && curNum === expected) {
                    return;
                }
                inst.setFormValue("codigo", expected, "number");
            }).catch(() => {
                inst.setFormValue("codigo", expected, "number");
            });
        };

        const rafId = requestAnimationFrame(syncCodigoIfNeeded);
        return () => cancelAnimationFrame(rafId);
    }, [id, initialData, resolvedFormConfig]);

    useEffect(() => {
        const unsubscribe = CurrencyIdPubSub.subscribe(setFormCurrency);
        return () => {
            unsubscribe();
        };
    }, []);

    useEffect(() => {
        if (currencyId) {
            setTimeout(() => {
                CurrencyIdPubSub.publish({ currencyId: currencyId, forceUpload: false })
            });
        }
    }, [currencyId]);

    const setFormCurrency = async (payload: CurrencyUpdatePubSubPayload) => {
        const { currencyId, forceUpload = true } = payload;
        const priceData = await CurrencyApi.get(currencyId);
        if (priceData.currency_id) {
            dispatch(setCurrency(removeFunctionFromObject(priceData)));
            setCurrencySymbol(CURRENCIES_SYMBOLS[priceData.currency_id])
            if (formRef) {
                const currentFormData = await formRef.current?.getFormData(false);
                if (forceUpload || !currentFormData.exchange_rate) {
                    formRef.current?.setFormValue("exchange_rate", priceData.exchange_rate, "decimal")
                }
            }
        }
    }

    useEffect(() => {
        const run = async () => {
            const loadData = async (id: string) => {
                try {
                    let data = {} as TApiData
                    if (getById) {
                        data = await getById(id);
                        if (copyData) {
                            data = omitProperties(data, ["createdAt", "updatedAt", ...(copyExcludeProps as string[])]) as TApiData;
                        }
                    }
                    setInitialData(data);
                } finally {
                    setIsLoading(false);
                }
            };
            if (id) {
                setIsLoading(true);
                loadData(id);
            } else {
                let nextInitial = {
                    ...(defaultData ?? {}),
                } as TApiData;

                if (getNextCode) {
                    const nextCode = await getNextCode();
                    nextInitial = {
                        ...nextInitial,
                        codigo: nextCode,
                    };
                }

                setInitialData(nextInitial);
            }
        }
        run();
    }, [id]);

    const onSubmit = async (formData: Partial<TFormData>) => {
        setIsLoading(true);

        if (copyData) {
            delete formData.id;
        }
        if (modelRelativeObjects && modelRelativeObjects.length) {
            modelRelativeObjects.forEach(o => {
                delete formData[o]
            })
        }

        try {
            let storeObject;
            if (!formData?.id) {
                if (createFn) {
                    storeObject = await createFn(formData as TApiData);
                }
            } else {
                if (updateFn) {
                    storeObject = await updateFn(formData.id, formData as TApiData);
                }
            }
            if (successMessage) {
                showAlert("success", successMessage);
            }
            if (onStored) {
                onStored(storeObject)
            }
            if (saveRedirectUrl) {
                navigate(saveRedirectUrl);
            }
        } catch (err) {
            if (errorMessage) {
                showAlert("danger", errorMessage);
            }
            showAlert("danger", (err as Error).message);
        } finally {
            setIsLoading(false);
        }
    };

    const dynamicFormMountKey =
        id != null && id !== ""
            ? id
            : `create-${(initialData as { codigo?: unknown }).codigo != null ? String((initialData as { codigo?: unknown }).codigo) : "pending"}`;

    return (
        <DynamicForm<TFormData>
            key={dynamicFormMountKey}
            id={formId}
            ref={formRef}
            externalFormState={initialData}
            formConfig={resolvedFormConfig}
            isLoading={isLoading}
            onSubmit={onSubmit}
            onInputChange={onInputChange}
            onInputKeyDown={onInputKeyDown}
            confirmationConfig={confirmationConfig}
        >
            {children}
        </DynamicForm>
    );
};

const GenericForm = forwardRef(GenericFormInner) as <TFormData extends { id?: string | null }, TApiData extends Partial<TFormData>>(
    props: BaseFormProps<TFormData, TApiData> & { ref?: React.Ref<DynamicFormRef> }
) => ReturnType<typeof GenericFormInner>;

export default GenericForm;